/**
 * The ⌘K overlay (DESIGN §1 overlays, §2.2 interaction table). Fuzzy-ish
 * grouped results from registered providers; keyboard: ↑/↓ move, Enter run,
 * Esc close. Sentence-shaped queries / no matches fall through to the
 * ask-the-guide result (WS6 upgrades ranking later via its own provider).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useFocusLayer } from '../../lib/useFocusLayer';
import { useUiStore } from '../../stores/uiStore';
import {
  queryPaletteProviders,
  runPaletteAction,
  type PaletteAction,
  type PaletteGroup,
  type PaletteResult,
} from './registry';

interface FlatResult extends PaletteResult {
  groupLabel: string;
}

function flatten(groups: PaletteGroup[]): FlatResult[] {
  return groups.flatMap((g) => g.results.map((r) => ({ ...r, groupLabel: g.label })));
}

/** The always-available fallback: hand the query to the guide. */
function askFallback(query: string): FlatResult {
  return {
    id: '__ask',
    label: `Ask the guide: “${query}”`,
    kind: 'ask',
    action: { type: 'ask-guide', text: query },
    groupLabel: 'Ask',
  };
}

export function PaletteOverlay() {
  const open = useUiStore((s) => s.paletteOpen);
  const setOpen = useUiStore((s) => s.setPaletteOpen);
  const [query, setQuery] = useState('');
  const [groups, setGroups] = useState<PaletteGroup[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  // Focus is the palette's while it is open (useFocusLayer): the search
  // takes it (autoFocus), Tab stays inside, and closing hands it back to
  // wherever ⌘K was pressed from.
  const dialogRef = useRef<HTMLDivElement>(null);
  useFocusLayer(dialogRef, open);

  // Global ⌘K / Ctrl+K
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(!useUiStore.getState().paletteOpen);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setOpen]);

  // Reset per open, in render: the palette never shows the last search for
  // a frame before clearing it.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setQuery('');
      setGroups([]);
      setActiveIndex(0);
    }
  }

  // Query providers
  useEffect(() => {
    if (!open) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    let cancelled = false;
    void queryPaletteProviders(query, controller.signal).then((result) => {
      if (!cancelled && !controller.signal.aborted) {
        setGroups(result);
        setActiveIndex(0);
      }
    });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [open, query]);

  const results = useMemo(() => {
    const flat = flatten(groups);
    if (query.trim().length > 0) {
      flat.push(askFallback(query.trim()));
    }
    return flat;
  }, [groups, query]);

  const close = useCallback(() => setOpen(false), [setOpen]);

  const run = useCallback(
    (action: PaletteAction) => {
      close();
      runPaletteAction(action);
    },
    [close],
  );

  if (!open) return null;

  return (
    <div
      // The backdrop: a pointer shortcut for Escape, nothing a screen reader
      // needs to hear.
      role="presentation"
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 pt-[15vh]"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        tabIndex={-1}
        className="w-[560px] overflow-hidden rounded-panel border border-default bg-raised shadow-overlay outline-none"
      >
        <input
          // eslint-disable-next-line jsx-a11y/no-autofocus -- a freshly opened palette: typing the search is the one thing to do
          autoFocus
          value={query}
          placeholder="Search areas, flows, services… or ask anything"
          aria-label="Palette search"
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              close();
            } else if (e.key === 'ArrowDown') {
              e.preventDefault();
              setActiveIndex((i) => Math.min(i + 1, results.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActiveIndex((i) => Math.max(i - 1, 0));
            } else if (e.key === 'Enter') {
              const active = results[activeIndex];
              if (active !== undefined) run(active.action);
            }
          }}
          className="h-12 w-full border-b border-default bg-transparent px-4 text-[15px] text-primary outline-none placeholder:text-tertiary"
        />
        <div className="max-h-[50vh] overflow-auto py-1" role="listbox">
          {results.length === 0 ? (
            <div className="px-4 py-6 text-center text-tertiary">Nothing yet. Keep typing.</div>
          ) : (
            results.map((r, i) => {
              const prev = results[i - 1];
              const showGroup = prev === undefined || prev.groupLabel !== r.groupLabel;
              return (
                <div key={`${r.groupLabel}:${r.id}`}>
                  {showGroup ? (
                    <div className="px-4 pt-2 pb-1 text-[length:var(--text-micro-size)] font-medium tracking-[var(--text-micro-tracking)] uppercase text-tertiary">
                      {r.groupLabel}
                    </div>
                  ) : null}
                  <button
                    type="button"
                    role="option"
                    aria-selected={i === activeIndex}
                    onMouseEnter={() => setActiveIndex(i)}
                    onClick={() => run(r.action)}
                    className={`flex w-full cursor-pointer items-baseline justify-between gap-3 px-4 py-2 text-left ${
                      i === activeIndex ? 'bg-accent-subtle' : ''
                    }`}
                  >
                    <span className="shrink-0 truncate text-primary">{r.label}</span>
                    {r.hint !== undefined ? (
                      // mono is for code/paths only — a flow's hint is prose
                      <span
                        className={`${r.kind === 'flow' ? '' : 'mono '}min-w-0 flex-1 truncate text-right text-[length:var(--text-ui-size)] text-tertiary`}
                      >
                        {r.hint}
                      </span>
                    ) : null}
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
